import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/AuthContext";
import { MarcaParametrosService } from "../../services/MarcaParametrosService";
import Preloader from "../../components/Preloader";
import { MarcasService } from "../../services/MarcasService";
import { Plus, Pencil, Trash2 } from "lucide-react";
import "./ParametrosMarca.css";
import Permiso from "../../router/Permiso";

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "textarea", label: "Área de texto" },
  { value: "number", label: "Número" },
  { value: "selector", label: "Selector" },
  { value: "boolean", label: "Booleano" },
  { value: "fecha", label: "Fecha" },
];

const ParametrosMarca = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const [marca, setMarca] = useState(null);
  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    nombre_campo: "",
    tipo_campo: "text",
    valores_selector: "",
    requerido: false,
  });

  const [selectorInput, setSelectorInput] = useState("");
  const [selectorOptions, setSelectorOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const fetchMarca = useCallback(async () => {
    try {
      const data = await MarcasService.fetchMarcaPorId({ token, id });
      setMarca(data);
    } catch (err) {
      console.error("Error al cargar marca:", err);
    }
  }, [token, id]);

  const fetchParametros = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await MarcaParametrosService.getByMarcaId({ token, marca_id: id });
      setParametros(data);
    } catch (err) {
      console.error("Error al cargar parámetros:", err);
      setError("Error al cargar los parámetros");
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    fetchMarca();
    fetchParametros();
  }, [fetchMarca, fetchParametros]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectorInputChange = (e) => {
    setSelectorInput(e.target.value);
  };

  const handleAddSelectorOption = () => {
    const value = selectorInput.trim();
    if (value && !selectorOptions.includes(value)) {
      setSelectorOptions((prev) => [...prev, value]);
      setSelectorInput("");
    }
  };

  const handleRemoveSelectorOption = (value) => {
    setSelectorOptions((prev) => prev.filter((v) => v !== value));
  };

  const handleSelectorKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSelectorOption();
    }
  };

  const openNewModal = () => {
    setEditingId(null);
    setFormData({
      nombre_campo: "",
      tipo_campo: "text",
      valores_selector: "",
      requerido: false,
    });
    setSelectorInput("");
    setSelectorOptions([]);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (parametro) => {
    setEditingId(parametro.id);
    setFormData({
      nombre_campo: parametro.nombre_campo,
      tipo_campo: parametro.tipo_campo,
      valores_selector: parametro.valores_selector || "",
      requerido: parametro.requerido === 1,
    });
    setSelectorInput("");
    setSelectorOptions(
      parametro.valores_selector
        ? JSON.parse(parametro.valores_selector)
        : []
    );
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errores = {};

    if (!formData.nombre_campo.trim()) {
      errores.nombre_campo = "El nombre del campo es obligatorio";
    }

    if (formData.tipo_campo === "selector" && selectorOptions.length === 0) {
      errores.valores_selector = "Debes agregar al menos un valor para el selector";
    }

    if (Object.keys(errores).length > 0) {
      setFormErrors(errores);
      return;
    }

    setLoading(true);
    try {
      const data = {
        marca_id: Number.parseInt(id),
        nombre_campo: formData.nombre_campo.trim(),
        tipo_campo: formData.tipo_campo,
        valores_selector: formData.tipo_campo === "selector" ? JSON.stringify(selectorOptions) : null,
        requerido: formData.requerido,
      };

      if (editingId) {
        await MarcaParametrosService.editarParametro({ token, id: editingId, data });
        toast.success("Parámetro actualizado correctamente");
      } else {
        await MarcaParametrosService.crearParametro({ token, data });
        toast.success("Parámetro creado correctamente");
      }

      handleCloseModal();
      await fetchParametros();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Error al guardar el parámetro");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (parametroId) => {
    if (!window.confirm("¿Está seguro de eliminar este parámetro?")) return;

    try {
      await MarcaParametrosService.eliminarParametro({ token, id: parametroId });
      toast.success("Parámetro eliminado correctamente");
      await fetchParametros();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || "Error al eliminar el parámetro";
      toast.error(msg);
    }
  };

  const getTipoLabel = (tipo) => {
    const found = FIELD_TYPES.find((ft) => ft.value === tipo);
    return found ? found.label : tipo;
  };

  const getTipoBadgeClass = (tipo) => {
    const classes = {
      text: "badge-text",
      textarea: "badge-textarea",
      number: "badge-number",
      selector: "badge-selector",
      boolean: "badge-boolean",
      fecha: "badge-fecha",
    };
    return classes[tipo] || "badge-text";
  };

  return (
    <div className="marcas-admin-content parametros-marca-page">
      <div className="marcas-admin-header">
        <div className="marcas-admin-header-row">
          <div>
            <div className="marcas-admin-title">Parámetros de {marca?.descripcion || "Marca"}</div>
          </div>
          <div className="marcas-admin-actions">
            <Permiso permiso="add_marcas">
              <button className="btn-nuevo" onClick={openNewModal}>
                <Plus size={18} style={{ marginRight: "6px" }} />
                Nuevo Parámetro
              </button>
            </Permiso>
          </div>
        </div>

      </div>
      <div className="marcas-admin-container">

        {loading && <Preloader />}
        <div className="breadcrumb-container">
          <button type="button" className="breadcrumb-link" onClick={() => navigate("/admin/marcas")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--color-primary)", textDecoration: "underline", font: "inherit" }}>
            {"\u2190"} Listado de Marcas
          </button>
          <span className="breadcrumb-current"> / Parámetros de {marca?.descripcion || ""}</span>
        </div>

        <div className="dashboard-card nuevo-producto-card">

          {error && <div className="error-message">{error}</div>}

          <div className="parametros-table-wrapper">
            <table className="parametros-table">
              <thead>
                <tr>
                  <th>Nombre del Campo</th>
                  <th>Tipo</th>
                  <th>Requerido</th>
                  <th>Valores Selector</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {parametros.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">
                      <div className="no-data-content">
                        <span>{"\uD83D\uDCCB"}</span>
                        <p>No hay parámetros configurados para esta marca</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  parametros.map((parametro, index) => (
                    <tr key={parametro.id} className="parametro-row">
                      <td className="parametro-nombre">{parametro.nombre_campo}</td>
                      <td className="parametro-tipo">
                        <span className={`badge ${getTipoBadgeClass(parametro.tipo_campo)}`}>
                          {getTipoLabel(parametro.tipo_campo)}
                        </span>
                      </td>
                      <td className="parametro-requerido">
                        {parametro.requerido === 1 ? (
                          <span className="requerido-si">Sí</span>
                        ) : (
                          <span className="requerido-no">No</span>
                        )}
                      </td>
                      <td className="parametro-valores">
                        {parametro.tipo_campo === "selector" && parametro.valores_selector ? (
                          <div className="selector-values">
                            {JSON.parse(parametro.valores_selector).map((val) => (
                              <span key={val} className="selector-value">
                                {val}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="no-values">—</span>
                        )}
                      </td>
                      <td className="parametro-acciones">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => openEditModal(parametro)}
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(parametro.id)}
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showModal && (
          <div className="modal-overlay-import">
            <div className="modal-box-import">
              <div className="modal-header">
                <span>
                  {editingId ? "Editar Parámetro" : "Nuevo Parámetro"}
                </span>
                <button className="modal-close-btn" onClick={handleCloseModal}>
                  ×
                </button>
              </div>
              <div className="modal-content-import">
                <form onSubmit={handleSubmit} className="nuevo-producto-form">
                  <div className="form-section">
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="nombre_campo">Nombre del Campo *</label>
                        <input
                          type="text"
                          id="nombre_campo"
                          name="nombre_campo"
                          value={formData.nombre_campo}
                          onChange={handleChange}
                          placeholder="Ej: color, tamaño, material"
                        />
                        {formErrors.nombre_campo && (
                          <span className="error">{formErrors.nombre_campo}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="tipo_campo">Tipo de Campo *</label>
                        <select
                          id="tipo_campo"
                          name="tipo_campo"
                          value={formData.tipo_campo}
                          onChange={handleChange}
                        >
                          {FIELD_TYPES.map((ft) => (
                            <option key={ft.value} value={ft.value}>
                              {ft.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className=" checkbox-center">
                        <label className="checkbox-label">
                         <span>Campo Requerido</span>
                          <input
                            type="checkbox"
                            name="requerido"
                            checked={formData.requerido}
                            onChange={handleChange}
                          />
                        </label>
                      </div>

                      {formData.tipo_campo === "selector" && (
                        <div className="form-group full-width">
                          <label htmlFor="selectorInput">Valores del Selector *</label>
                          <div className="selector-input-wrapper">
                            <input
                              type="text"
                              id="selectorInput"
                              value={selectorInput}
                              onChange={handleSelectorInputChange}
                              onKeyDown={handleSelectorKeyDown}
                              placeholder="Escribir valor y presionar Enter o +"
                            />
                            <button
                              type="button"
                              className="btn-add-selector"
                              onClick={handleAddSelectorOption}
                            >
                              +
                            </button>
                          </div>
                          {selectorOptions.length > 0 && (
                            <div className="selector-tags">
                              {selectorOptions.map((opt) => (
                                <span key={opt} className="selector-tag">
                                  {opt}
                                  <button
                                    type="button"
                                    className="selector-tag-remove"
                                    onClick={() => handleRemoveSelectorOption(opt)}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          {formErrors.valores_selector && (
                            <span className="error">{formErrors.valores_selector}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                     className="cancel-btn"
                      onClick={handleCloseModal}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                     className="save-btn"
                      disabled={loading}
                    >
                    {(() => {
                      if (loading) return "Guardando...";
                      if (editingId) return "Actualizar";
                      return "Guardar";
                    })()}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParametrosMarca;
